import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  BellRing,
  BrainCircuit,
  BookOpenCheck,
  Briefcase,
  Building2,
  CalendarClock,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  FileClock,
  FileSignature,
  KeyRound,
  LifeBuoy,
  ListChecks,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Tags,
  UserCog,
  UsersRound,
  X
} from 'lucide-react';
import { useAuthStore } from '../../store';
import { ADMIN_NAVIGATION_GROUPS } from '../../constants';
import { getDefaultAllowedPath, hasPermission } from '../../utils/rbac';
import logoMark from '../../assets/images/sanspath-logo-mark.png';

const iconMap = {
  Activity,
  BarChart3,
  BellRing,
  BookOpenCheck,
  BrainCircuit,
  Briefcase,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList: ListChecks,
  CreditCard,
  FileClock,
  FileSignature,
  LifeBuoy,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Tags,
  UserCog,
  UsersRound
};

const isRouteActive = (pathname, href) => (
  href === '/dashboard' ? pathname === '/dashboard' || pathname === '/' : pathname.startsWith(href)
);

const resolveIcon = (icon) => iconMap[icon] || Settings;

const getVisibleNavigation = (user) => ADMIN_NAVIGATION_GROUPS.reduce((items, item) => {
  if (item.children?.length) {
    const children = item.children.filter((child) => hasPermission(user, child.permission));
    if (children.length) items.push({ ...item, children });
    return items;
  }

  if (hasPermission(user, item.permission)) items.push(item);
  return items;
}, []);

const Sidebar = ({ isOpen, onToggle, onNavigate, isMobile: isMobileProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const isMobile = Boolean(isMobileProp);

  const handleLinkClick = (path) => {
    if (onNavigate) {
      onNavigate(path);
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isMobile) {
    return (
      <>
        <div
          className={`fixed inset-0 z-40 bg-slate-900/50 transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={onToggle}
        />

        <aside
          className={`fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col border-r border-slate-200 bg-white transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarBody
            expanded
            user={user}
            pathname={location.pathname}
            onNavigate={handleLinkClick}
            onLogout={handleLogout}
            onClose={onToggle}
          />
        </aside>
      </>
    );
  }

  return (
    <aside className={`relative hidden h-full shrink-0 border-r border-slate-200 bg-white transition-[width] duration-300 lg:flex ${
      isOpen ? 'w-[260px]' : 'w-[72px]'
    }`}>
      <SidebarBody
        expanded={isOpen}
        isMobile={false}
        user={user}
        pathname={location.pathname}
        onNavigate={handleLinkClick}
        onLogout={handleLogout}
        onToggle={onToggle}
      />
    </aside>
  );
};

const SidebarBody = ({ expanded, isMobile = true, user, pathname, onNavigate, onLogout, onClose, onToggle }) => {
  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.trim() || 'A';
  const visibleNavigation = useMemo(() => getVisibleNavigation(user), [user]);
  const [openGroups, setOpenGroups] = useState({});
  const homePath = getDefaultAllowedPath(user);
  const profileActive = isRouteActive(pathname, '/profile');
  const guideActive = isRouteActive(pathname, '/user-guide');

  useEffect(() => {
    const activeGroup = visibleNavigation.find((item) => (
      item.children?.some((child) => isRouteActive(pathname, child.path))
    ));

    if (activeGroup) {
      setOpenGroups((current) => ({ ...current, [activeGroup.key]: true }));
    }
  }, [pathname, visibleNavigation]);

  const toggleGroup = (key) => {
    setOpenGroups((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <div className={`relative flex h-full w-full flex-col overflow-hidden py-5 ${expanded ? '' : 'items-center px-2'}`}>
      <div className={`relative z-10 mb-8 flex w-full items-center ${expanded ? 'justify-between px-4' : 'flex-col justify-center gap-3'}`}>
        <button
          onClick={() => onNavigate(homePath)}
          className={`group flex items-center ${expanded ? 'gap-3' : 'justify-center'} rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400`}
          aria-label="Go to dashboard"
        >
          <img
            src={logoMark}
            alt="Sanspath"
            className={`shrink-0 object-contain ${expanded ? 'h-7 w-7' : 'h-8 w-8'}`}
          />
          {expanded && (
            <div className="flex flex-col items-start text-left">
              <span className="font-semibold text-slate-900 leading-tight">
                Sanspath
              </span>
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                Admin
              </span>
            </div>
          )}
        </button>

        {expanded && (
          <button
            onClick={isMobile ? onClose : onToggle}
            className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label={isMobile ? 'Close navigation' : 'Collapse navigation'}
          >
            {isMobile ? <X size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}

        {!expanded && !isMobile && (
          <button
            onClick={onToggle}
            className="flex h-10 w-10 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Expand navigation"
            title="Expand navigation"
          >
            <PanelLeftOpen size={18} />
          </button>
        )}
      </div>

      <nav className={`relative z-10 flex-1 overflow-y-auto custom-scrollbar ${expanded ? 'w-full space-y-1 px-4' : 'space-y-2'}`}>
        {visibleNavigation.map((item) => {
          const children = item.children || [];
          const firstChild = children[0];
          const isGroup = children.length > 0;
          const targetPath = item.path || firstChild?.path;
          const activeChild = isGroup
            ? [...children]
              .sort((a, b) => b.path.length - a.path.length)
              .find((child) => isRouteActive(pathname, child.path))
            : null;
          const active = isGroup ? Boolean(activeChild) : isRouteActive(pathname, item.path);
          const open = Boolean(openGroups[item.key] || active);
          const Icon = resolveIcon(item.icon);

          if (isGroup && expanded) {
            return (
              <div key={item.key} className="space-y-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(item.key)}
                  className={`
                    group relative flex h-10 w-full items-center gap-3 rounded-md px-3 text-left transition-colors
                    ${active
                      ? 'bg-slate-100 text-slate-900 font-medium'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                  aria-expanded={open}
                >
                  <Icon size={18} strokeWidth={active ? 2.5 : 2} className={`shrink-0 ${active ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-700'}`} />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {item.name}
                  </span>
                  <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? 'rotate-180 text-slate-900' : 'text-slate-400'}`} />
                </button>

                {open && (
                  <div className="ml-7 mt-1 space-y-1 border-l border-slate-200 pl-3">
                    {children.map((child) => {
                      const childActive = activeChild?.path === child.path;
                      const ChildIcon = resolveIcon(child.icon);

                      return (
                        <button
                          key={child.key}
                          type="button"
                          onClick={() => onNavigate(child.path)}
                          className={`
                            group flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-left transition-colors
                            ${childActive
                              ? 'bg-slate-100 text-slate-900 font-medium'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                            }
                          `}
                          aria-current={childActive ? 'page' : undefined}
                        >
                          <ChildIcon size={16} strokeWidth={childActive ? 2.5 : 2} className={`shrink-0 ${childActive ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'}`} />
                          <span className="min-w-0 truncate text-sm">
                            {child.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <button
              key={item.key || item.name}
              onClick={() => onNavigate(targetPath)}
              className={`
                group relative flex items-center rounded-md transition-colors
                ${expanded ? 'h-10 w-full gap-3 px-3 text-left' : 'h-10 w-10 justify-center mx-auto'}
                ${active
                  ? 'bg-slate-100 text-slate-900 font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }
              `}
              title={expanded ? undefined : item.name}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={18} strokeWidth={active ? 2.5 : 2} className={`shrink-0 ${active ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-700'}`} />
              {expanded && <span className="truncate text-sm">{item.name}</span>}
            </button>
          );
        })}
      <div className={`mt-6 rounded-[16px] bg-blue-600 p-2 shadow-sm ${expanded ? 'space-y-1' : 'flex flex-col items-center space-y-2'}`}>
        <button
          type="button"
          onClick={() => onNavigate('/profile')}
          className={`group flex items-center rounded-md text-left transition-colors hover:bg-blue-700/50 ${
            expanded ? 'mb-2 h-12 w-full gap-3 px-2' : 'h-10 w-10 justify-center'
          } ${profileActive ? 'bg-blue-700' : ''}`}
          title={expanded ? undefined : 'My profile'}
          aria-current={profileActive ? 'page' : undefined}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-xs font-bold text-blue-700 shadow-sm">
            {initials}
          </span>
          {expanded && (
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">
                {user?.firstName ? `${user.firstName} ${user?.lastName || ''}`.trim() : 'Admin'}
              </span>
              <span className="block truncate text-xs text-blue-100/90">
                {user?.adminRole?.replace(/_/g, ' ') || user?.email || 'Admin access'}
              </span>
            </span>
          )}
        </button>

        <button
          onClick={() => onNavigate('/user-guide')}
          className={`group flex items-center rounded-md transition-colors ${
            expanded ? 'mb-1 h-10 w-full gap-3 px-3 text-sm font-medium' : 'h-10 w-10 justify-center'
          } ${
            guideActive
              ? 'bg-blue-700 text-white'
              : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
          }`}
          title={expanded ? undefined : 'User guide'}
          aria-current={guideActive ? 'page' : undefined}
        >
          <BookOpenCheck size={18} className="shrink-0" />
          {expanded && <span>User guide</span>}
        </button>

        <button
          onClick={() => onNavigate('/profile')}
          className={`group flex items-center rounded-md transition-colors ${
            expanded ? 'mb-1 h-10 w-full gap-3 px-3 text-sm font-medium' : 'h-10 w-10 justify-center'
          } ${
            profileActive
              ? 'bg-blue-700 text-white'
              : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
          }`}
          title={expanded ? undefined : 'My profile'}
          aria-current={profileActive ? 'page' : undefined}
        >
          <KeyRound size={18} className="shrink-0" />
          {expanded && <span>My profile</span>}
        </button>
      </div>
      </nav>

      <div className={`relative z-10 shrink-0 flex flex-col ${expanded ? 'w-full px-4 pt-4' : 'w-full pt-4 items-center'}`}>
        <button
          onClick={onLogout}
          className={`group flex items-center rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-600 transition-colors ${
            expanded ? 'h-10 w-full gap-3 px-3 text-sm font-medium' : 'h-10 w-10 justify-center'
          }`}
          title={expanded ? undefined : 'Log out'}
        >
          <LogOut size={18} className="shrink-0" />
          {expanded && <span>Log out</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
